import styled from 'styled-components'
import { colors, fontWeights } from '@/styles'
import { CssUnit } from '@/types'
import { toUnit } from '@/utils/styles'

type BubbleTuple = [string, string]

const BUBBLE_ANALYZE: BubbleTuple = [colors.blue64, colors.white]
const BUBBLE_GREEN: BubbleTuple = ['#62ad83', colors.white]
const BUBBLE_NEUTRAL: BubbleTuple = [colors.grey88, colors.grey48]
const BUBBLE_RED: BubbleTuple = ['#dc0027', colors.white]
const BUBBLE_SUBMITTED: BubbleTuple = [colors.green96, colors.green32]

const BUBBLE_COLORS: Record<string, BubbleTuple> = {
  // Card bubble, use opacity colors
  'card-analyzing': BUBBLE_ANALYZE,
  'card-pending': [colors.grey72, colors.black],
  'card-excellent': ['rgba(255, 255, 255, 0.1)', '#ffc42c'],
  'card-very good': ['rgba(255, 255, 255, 0.1)', colors.white],
  'card-average': ['rgba(255, 255, 255, 0.1)', colors.white],
  'card-poor': ['rgba(193, 12, 0, 0.2)', '#c10c00'],
  // Payments: all, trustworthiness: no neutral
  '-': BUBBLE_NEUTRAL,
  analyzing: BUBBLE_ANALYZE,
  excellent: ['#23537e', '#ffc42c'],
  'very good': ['#528bdf', colors.white],
  average: BUBBLE_GREEN,
  poor: ['#f9d580', '#c7210e'],
  // Can afford: verified, not_verified
  verified: BUBBLE_GREEN,
  not_verified: BUBBLE_RED,
  // Court: failed, passed, pending
  failed: BUBBLE_RED,
  passed: BUBBLE_GREEN,
  pending: BUBBLE_NEUTRAL,
  submitted: BUBBLE_SUBMITTED,
  requested: BUBBLE_NEUTRAL
}

type Align = 'center' | 'flex-start' | 'flex-end'
type Size = 'small' | 'normal'

const BubbleText = styled.span<{ $color: BubbleTuple; top: CssUnit; align: Align; size: Size }>`
  align-self: ${props => props.align};
  margin: ${props => toUnit(props.top)} 0 0;
  border-radius: 9999px;
  font-weight: ${fontWeights.medium};
  white-space: nowrap;
  ${props =>
    props.$color &&
    `
    background-color: ${props.$color[0]};
    color: ${props.$color[1]};
  `};

  ${props =>
    props.size === 'small'
      ? `
    font-size: 9.25px;
    letter-spacing: -0.05px;
    line-height: 12px;
    padding: 3px 7px;
  `
      : `
    font-size: 12px;
    letter-spacing: -0.24px;
    line-height: 16px;
    padding: 4px 8px;
  `};
`

type BubbleProps = {
  children: React.ReactNode
  label?: string
  color: string
  top?: CssUnit
  align?: Align
  size?: Size
}

const getBubbleColor = (key: string): BubbleTuple => BUBBLE_COLORS[key] || BUBBLE_COLORS['-']

export const BubbleTitle: React.FC<BubbleProps> = ({
  children,
  color,
  top = 8,
  align = 'center',
  size = 'normal',
  label
}) => (
  <BubbleText
    $color={getBubbleColor(color)}
    top={top}
    align={align}
    size={size}
    title={label ? `${label}: ${children}` : undefined}
  >
    {children}
  </BubbleText>
)
